import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, Plus, Shield, AlertTriangle, XCircle,
  CheckCircle2, Calendar, Trash2, Edit2, ChevronDown, Ship
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  valid: { color: 'text-[#3CB371]', bg: 'bg-[#E8F6ED]', border: 'border-[#3CB371]', icon: CheckCircle2, label: 'Valid' },
  attention: { color: 'text-[#E5A000]', bg: 'bg-[#FFF8E1]', border: 'border-[#E5A000]', icon: AlertTriangle, label: 'Attention' },
  expiring_soon: { color: 'text-[#FF6B35]', bg: 'bg-[#FFF0EA]', border: 'border-[#FF6B35]', icon: AlertTriangle, label: 'Expiring Soon' },
  expired: { color: 'text-[#D32F2F]', bg: 'bg-[#FAEAEA]', border: 'border-[#D32F2F]', icon: XCircle, label: 'Expired' },
  unknown: { color: 'text-[#4A5568]', bg: 'bg-[#F0F4F8]', border: 'border-[#E2E8F0]', icon: Shield, label: 'Unknown' },
};

function AddEquipmentForm({ vessels, types, onSave, onCancel, editItem }) {
  const [form, setForm] = useState({
    vessel_id: editItem?.vessel_id || '',
    type_id: editItem?.type_id || '',
    name: editItem?.name || '',
    brand: editItem?.brand || '',
    serial_number: editItem?.serial_number || '',
    purchase_date: editItem?.purchase_date?.split('T')[0] || '',
    expiry_date: editItem?.expiry_date?.split('T')[0] || '',
    notes: editItem?.notes || '',
    notify_days_before: editItem?.notify_days_before || 30,
  });

  const selectedType = types.find(t => t.id === form.type_id);

  const handleTypeChange = (typeId) => {
    const type = types.find(t => t.id === typeId);
    setForm(prev => ({
      ...prev,
      type_id: typeId,
      name: prev.name || type?.name || '',
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.expiry_date) {
      toast.error('Name and expiry date are required');
      return;
    }
    onSave({
      ...form,
      expiry_date: new Date(form.expiry_date).toISOString(),
      purchase_date: form.purchase_date ? new Date(form.purchase_date).toISOString() : null,
    });
  };

  return (
    <Card className="border-2 border-[#2B3A67]">
      <CardContent className="p-5">
        <h3 className="font-bold text-[#2B3A67] text-lg mb-4 font-['Outfit']">
          {editItem ? 'Edit Equipment' : 'Track New Equipment'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Vessel */}
          {vessels.length > 0 && (
            <div>
              <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Vessel</label>
              <select
                data-testid="equipment-vessel-select"
                value={form.vessel_id}
                onChange={e => setForm(prev => ({ ...prev, vessel_id: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] bg-white text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
              >
                <option value="">All Vessels</option>
                {vessels.map(v => (
                  <option key={v.vessel_id} value={v.vessel_id}>{v.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Equipment Type */}
          <div>
            <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Equipment Type</label>
            <select
              data-testid="equipment-type-select"
              value={form.type_id}
              onChange={e => handleTypeChange(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] bg-white text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
            >
              <option value="">Select type...</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Name / Label</label>
            <input
              data-testid="equipment-name-input"
              type="text"
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Kidde Marine Fire Ext."
              className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
              required
            />
          </div>

          {/* Brand + Serial */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Brand</label>
              <input
                data-testid="equipment-brand-input"
                type="text"
                value={form.brand}
                onChange={e => setForm(prev => ({ ...prev, brand: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Serial #</label>
              <input
                data-testid="equipment-serial-input"
                type="text"
                value={form.serial_number}
                onChange={e => setForm(prev => ({ ...prev, serial_number: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Purchase Date</label>
              <input
                data-testid="equipment-purchase-date"
                type="date"
                value={form.purchase_date}
                onChange={e => setForm(prev => ({ ...prev, purchase_date: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Expiry Date *</label>
              <input
                data-testid="equipment-expiry-date"
                type="date"
                value={form.expiry_date}
                onChange={e => setForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
                required
              />
            </div>
          </div>

          {selectedType && !form.expiry_date && (
            <p className="text-xs text-[#4A5568]">
              Typical lifespan: {selectedType.typical_lifespan_years} years from purchase
            </p>
          )}

          {/* Notify */}
          <div>
            <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Notify me before expiry</label>
            <select
              data-testid="equipment-notify-select"
              value={form.notify_days_before}
              onChange={e => setForm(prev => ({ ...prev, notify_days_before: parseInt(e.target.value) }))}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] bg-white text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
            >
              <option value={7}>1 week before</option>
              <option value={14}>2 weeks before</option>
              <option value={30}>1 month before</option>
              <option value={60}>2 months before</option>
              <option value={90}>3 months before</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Notes</label>
            <textarea
              data-testid="equipment-notes-input"
              value={form.notes}
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional details..."
              rows={2}
              className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-[#E2E8F0] text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1 h-12 border-2 border-[#E2E8F0] rounded-xl font-bold"
            >
              Cancel
            </Button>
            <Button
              data-testid="equipment-save-btn"
              type="submit"
              className="flex-1 h-12 bg-[#3CB371] hover:bg-[#2A8A53] text-white rounded-xl font-bold"
            >
              {editItem ? 'Update' : 'Add Equipment'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default function EquipmentExpiryPage() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterVessel, setFilterVessel] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [equipRes, vesselRes, typeRes] = await Promise.all([
        axios.get(`${API}/equipment`, { withCredentials: true }),
        axios.get(`${API}/vessels`, { withCredentials: true }),
        axios.get(`${API}/equipment/types`),
      ]);
      setEquipment(equipRes.data);
      setVessels(vesselRes.data);
      setTypes(typeRes.data);
    } catch (err) {
      console.error('Failed to fetch equipment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editItem) {
        await axios.put(`${API}/equipment/${editItem.equipment_id}`, data, { withCredentials: true });
        toast.success('Equipment updated');
      } else {
        await axios.post(`${API}/equipment`, data, { withCredentials: true });
        toast.success('Equipment added');
      }
      setShowForm(false);
      setEditItem(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save equipment');
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!window.confirm('Remove this equipment from tracking?')) return;
    try {
      await axios.delete(`${API}/equipment/${equipmentId}`, { withCredentials: true });
      toast.success('Equipment removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove equipment');
    }
  };

  const handleCheckExpiry = async () => {
    try {
      const res = await axios.post(`${API}/equipment/check-expiry`, {}, { withCredentials: true });
      if (res.data.notifications_sent > 0) {
        toast.success(`Sent ${res.data.notifications_sent} expiry notification(s)`);
      } else {
        toast.info('No expiring equipment found');
      }
    } catch (err) {
      toast.error('Failed to check expiry');
    }
  };

  const filteredEquipment = filterVessel
    ? equipment.filter(e => e.vessel_id === filterVessel)
    : equipment;

  const expiredCount = equipment.filter(e => e.status === 'expired').length;
  const expiringCount = equipment.filter(e => e.status === 'expiring_soon').length;

  const getVesselName = (vesselId) => {
    const v = vessels.find(v => v.vessel_id === vesselId);
    return v?.name || 'All Vessels';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8] pb-24 page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            data-testid="equipment-back-btn"
            onClick={() => navigate('/dashboard')}
            className="text-[#2B3A67]"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-xl font-bold text-[#2B3A67] font-['Outfit']">Equipment Tracker</h1>
          <Button
            variant="ghost"
            size="icon"
            data-testid="equipment-add-btn"
            onClick={() => { setEditItem(null); setShowForm(true); }}
            className="text-[#3CB371]"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-5">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-black text-[#2B3A67]">{equipment.length}</p>
              <p className="text-xs text-[#4A5568] font-medium">Tracked</p>
            </CardContent>
          </Card>
          <Card className={`border-2 ${expiringCount > 0 ? 'border-[#FF6B35] bg-[#FFF0EA]' : 'border-[#E2E8F0]'}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-black ${expiringCount > 0 ? 'text-[#FF6B35]' : 'text-[#4A5568]'}`}>{expiringCount}</p>
              <p className="text-xs text-[#4A5568] font-medium">Expiring</p>
            </CardContent>
          </Card>
          <Card className={`border-2 ${expiredCount > 0 ? 'border-[#D32F2F] bg-[#FAEAEA]' : 'border-[#E2E8F0]'}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-black ${expiredCount > 0 ? 'text-[#D32F2F]' : 'text-[#4A5568]'}`}>{expiredCount}</p>
              <p className="text-xs text-[#4A5568] font-medium">Expired</p>
            </CardContent>
          </Card>
        </div>

        {/* Alert Banner */}
        {(expiredCount > 0 || expiringCount > 0) && (
          <Card className={`border-2 ${expiredCount > 0 ? 'border-[#D32F2F] bg-[#FAEAEA]' : 'border-[#FF6B35] bg-[#FFF0EA]'}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${expiredCount > 0 ? 'text-[#D32F2F]' : 'text-[#FF6B35]'}`} />
              <div className="flex-1">
                <p className={`font-bold text-sm ${expiredCount > 0 ? 'text-[#D32F2F]' : 'text-[#FF6B35]'}`}>
                  {expiredCount > 0
                    ? `${expiredCount} item${expiredCount > 1 ? 's' : ''} expired!`
                    : `${expiringCount} item${expiringCount > 1 ? 's' : ''} expiring soon`}
                </p>
                <p className="text-xs text-[#4A5568] mt-0.5">
                  {expiredCount > 0 ? 'Replace to maintain FWC compliance' : 'Plan replacements ahead of time'}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vessel Filter */}
        {vessels.length > 1 && (
          <div className="flex items-center gap-2">
            <Ship className="w-4 h-4 text-[#4A5568]" />
            <select
              data-testid="equipment-filter-vessel"
              value={filterVessel}
              onChange={e => setFilterVessel(e.target.value)}
              className="flex-1 px-3 py-2 rounded-xl border-2 border-[#E2E8F0] bg-white text-sm text-[#2B3A67] font-medium focus:border-[#2B3A67] focus:outline-none"
            >
              <option value="">All Vessels</option>
              {vessels.map(v => (
                <option key={v.vessel_id} value={v.vessel_id}>{v.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Add Form */}
        {showForm && (
          <AddEquipmentForm
            vessels={vessels}
            types={types}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditItem(null); }}
            editItem={editItem}
          />
        )}

        {/* Equipment List */}
        {filteredEquipment.length === 0 && !showForm ? (
          <Card className="border-2 border-dashed border-[#E2E8F0]">
            <CardContent className="p-8 text-center">
              <Shield className="w-14 h-14 mx-auto text-[#4A5568] mb-3" />
              <p className="font-bold text-[#2B3A67] text-lg">No equipment tracked yet</p>
              <p className="text-sm text-[#4A5568] mt-1 mb-4">
                Add your fire extinguishers, flares, registration, and more to get expiry alerts
              </p>
              <Button
                data-testid="equipment-add-first-btn"
                onClick={() => setShowForm(true)}
                className="bg-[#3CB371] hover:bg-[#2A8A53] text-white font-bold rounded-xl h-12 px-6"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Equipment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredEquipment.map((item) => {
              const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.unknown;
              const StatusIcon = config.icon;
              return (
                <Card
                  key={item.equipment_id}
                  data-testid={`equipment-card-${item.equipment_id}`}
                  className={`border-2 ${config.border} transition-colors`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                          <StatusIcon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#2B3A67] truncate">{item.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${config.bg} ${config.color}`}>
                              {config.label}
                            </span>
                            {item.vessel_id && (
                              <span className="text-xs text-[#4A5568]">{getVesselName(item.vessel_id)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-2 text-xs text-[#4A5568]">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {item.status === 'expired'
                                ? `Expired ${formatDate(item.expiry_date)}`
                                : `Expires ${formatDate(item.expiry_date)}`}
                            </span>
                            {item.days_until_expiry !== null && item.days_until_expiry >= 0 && (
                              <span className={`font-bold ml-1 ${config.color}`}>
                                ({item.days_until_expiry}d left)
                              </span>
                            )}
                            {item.days_until_expiry !== null && item.days_until_expiry < 0 && (
                              <span className="font-bold ml-1 text-[#D32F2F]">
                                ({Math.abs(item.days_until_expiry)}d overdue)
                              </span>
                            )}
                          </div>
                          {item.brand && (
                            <p className="text-xs text-[#4A5568] mt-1">{item.brand}{item.serial_number ? ` - ${item.serial_number}` : ''}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`equipment-edit-${item.equipment_id}`}
                          onClick={() => { setEditItem(item); setShowForm(true); }}
                          className="h-8 w-8 text-[#4A5568] hover:text-[#2B3A67]"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`equipment-delete-${item.equipment_id}`}
                          onClick={() => handleDelete(item.equipment_id)}
                          className="h-8 w-8 text-[#4A5568] hover:text-[#D32F2F]"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Check Expiry Button */}
        {equipment.length > 0 && (
          <Button
            data-testid="check-expiry-btn"
            onClick={handleCheckExpiry}
            variant="outline"
            className="w-full h-12 border-2 border-[#2B3A67] text-[#2B3A67] font-bold rounded-xl"
          >
            <AlertTriangle className="w-5 h-5 mr-2" />
            Check & Send Expiry Alerts
          </Button>
        )}
      </main>
    </div>
  );
}
